/**
 * Vendor Mapper
 * Maps between API DTOs and Domain Entities
 */

import {
  VendorEntity,
  VendorSearchItemEntity,
  VendorSearchResultEntity,
  VendorOperationResult,
  VendorDetailResult,
  VendorSearchPagination,
} from "../../domain/entities/VendorEntity";
import {
  VendorDataDTO,
  VendorSearchItemDTO,
  VendorSearchResponseDTO,
  VendorResponseDTO,
  VendorDetailResponseDTO,
  VendorSearchPaginationDTO,
} from "../models/VendorDTOs";

export class VendorMapper {
  /**
   * Map VendorDataDTO to VendorEntity
   */
  static toVendorEntity(dto: VendorDataDTO): VendorEntity {
    return {
      vendorid: dto.vendorid,
      zoho_vendorid: dto.zoho_vendorid,
      vendor_legal_name: dto.vendor_legal_name,
      vendor_legal_entity: dto.vendor_legal_entity,
      type_of_vendor: dto.type_of_vendor,
      business_category: dto.business_category,
      trading_name: dto.trading_name,
      group_parent_entity: dto.group_parent_entity,
      vendor_classification: dto.vendor_classification,
      registered_office_address: dto.registered_office_address,
      country: dto.country,
      state: dto.state,
      city: dto.city,
      postal_code: dto.postal_code,
      currency_id: dto.currency_id,
      correspondence_address: dto.correspondence_address,
      beneficiary_address: dto.beneficiary_address,
      primary_contact_name: dto.primary_contact_name,
      primary_contact_phone: dto.primary_contact_phone,
      primary_contact_email: dto.primary_contact_email,
      alternate_contact: dto.alternate_contact,
      escalation_contact: dto.escalation_contact,
      website: dto.website,
      pan_tax_id: dto.pan_tax_id,
      gstin: dto.gstin,
      msme_flag: dto.msme_flag,
      company_registration_number: dto.company_registration_number,
      import_export_code: dto.import_export_code,
      iso_certifications: dto.iso_certifications,
      regulatory_licenses: dto.regulatory_licenses,
      udyam_number: dto.udyam_number,
      udyam_type: dto.udyam_type,
      beneficiary_name: dto.beneficiary_name,
      bank_account_number: dto.bank_account_number,
      bank_name: dto.bank_name,
      branch: dto.branch,
      ifsc_swift_code: dto.ifsc_swift_code,
      payment_terms: dto.payment_terms,
      currency: dto.currency,
      currency_symbol: dto.currency_symbol,
      preferred_payment_method: dto.preferred_payment_method,
      advance_security_deposit: dto.advance_security_deposit,
      credit_limit: dto.credit_limit,
      gst_category: dto.gst_category,
      gst_treatment: dto.gst_treatment as any,
      tds_applicable: dto.tds_applicable,
      party_type: dto.party_type,
      tds_tax_id: dto.tds_tax_id,
      exemption_certificates: dto.exemption_certificates,
      typical_hsn_sac_codes: dto.typical_hsn_sac_codes,
      risk_rating: dto.risk_rating,
      invoice_submission_method: dto.invoice_submission_method,
      status: dto.status,
      refresh_token: dto.refresh_token,
    };
  }

  /**
   * Map VendorEntity to VendorDataDTO
   */
  static toVendorDataDTO(entity: VendorEntity): VendorDataDTO {
    return {
      vendorid: entity.vendorid,
      zoho_vendorid: entity.zoho_vendorid,
      vendor_legal_name: entity.vendor_legal_name,
      vendor_legal_entity: entity.vendor_legal_entity,
      type_of_vendor: entity.type_of_vendor,
      business_category: entity.business_category,
      trading_name: entity.trading_name,
      group_parent_entity: entity.group_parent_entity,
      vendor_classification: entity.vendor_classification,
      registered_office_address: entity.registered_office_address,
      country: entity.country,
      state: entity.state,
      city: entity.city,
      postal_code: entity.postal_code,
      currency_id: entity.currency_id,
      correspondence_address: entity.correspondence_address,
      beneficiary_address: entity.beneficiary_address,
      primary_contact_name: entity.primary_contact_name,
      primary_contact_phone: entity.primary_contact_phone,
      primary_contact_email: entity.primary_contact_email,
      alternate_contact: entity.alternate_contact,
      escalation_contact: entity.escalation_contact,
      website: entity.website,
      pan_tax_id: entity.pan_tax_id,
      gstin: entity.gstin,
      msme_flag: entity.msme_flag,
      company_registration_number: entity.company_registration_number,
      import_export_code: entity.import_export_code,
      iso_certifications: entity.iso_certifications,
      regulatory_licenses: entity.regulatory_licenses,
      udyam_number: entity.udyam_number,
      udyam_type: entity.udyam_type,
      beneficiary_name: entity.beneficiary_name,
      bank_account_number: entity.bank_account_number,
      bank_name: entity.bank_name,
      branch: entity.branch,
      ifsc_swift_code: entity.ifsc_swift_code,
      payment_terms: entity.payment_terms,
      currency: entity.currency,
      currency_symbol: entity.currency_symbol,
      preferred_payment_method: entity.preferred_payment_method,
      advance_security_deposit: entity.advance_security_deposit,
      credit_limit: entity.credit_limit,
      gst_category: entity.gst_category,
      gst_treatment: entity.gst_treatment,
      tds_applicable: entity.tds_applicable,
      party_type: entity.party_type,
      tds_tax_id: entity.tds_tax_id,
      exemption_certificates: entity.exemption_certificates,
      typical_hsn_sac_codes: entity.typical_hsn_sac_codes,
      risk_rating: entity.risk_rating,
      invoice_submission_method: entity.invoice_submission_method,
      status: entity.status,
      refresh_token: entity.refresh_token,
    };
  }

  /**
   * Map VendorSearchItemDTO to VendorSearchItemEntity
   */
  static toVendorSearchItemEntity(
    dto: VendorSearchItemDTO
  ): VendorSearchItemEntity {
    return {
      vendorid: dto.vendorid,
      zoho_vendorid: dto.zoho_vendorid,
      vendor_legal_name: dto.vendor_legal_name,
      vendor_legal_entity: dto.vendor_legal_entity,
      type_of_vendor: dto.type_of_vendor,
      business_category: dto.business_category,
      trading_name: dto.trading_name,
      group_parent_entity: dto.group_parent_entity,
      vendor_classification: dto.vendor_classification,
      registered_office_address: dto.registered_office_address,
      country: dto.country,
      state: dto.state,
      city: dto.city,
      postal_code: dto.postal_code,
      primary_contact_name: dto.primary_contact_name,
      primary_contact_phone: dto.primary_contact_phone,
      primary_contact_email: dto.primary_contact_email,
      correspondence_address: dto.correspondence_address,
      alternate_contact: dto.alternate_contact,
      escalation_contact: dto.escalation_contact,
      website: dto.website,
      pan_tax_id: dto.pan_tax_id,
      gstin: dto.gstin,
      msme_flag: dto.msme_flag,
      company_registration_number: dto.company_registration_number,
      import_export_code: dto.import_export_code,
      iso_certifications: dto.iso_certifications,
      regulatory_licenses: dto.regulatory_licenses,
      udyam_number: dto.udyam_number,
      udyam_type: dto.udyam_type,
      beneficiary_name: dto.beneficiary_name,
      bank_account_number: dto.bank_account_number,
      bank_name: dto.bank_name,
      branch: dto.branch,
      ifsc_swift_code: dto.ifsc_swift_code,
      preferred_payment_method: dto.preferred_payment_method,
      beneficiary_address: dto.beneficiary_address,
      advance_security_deposit: dto.advance_security_deposit,
      withholding_tax_applicability: dto.withholding_tax_applicability,
      withholding_tax_rate: dto.withholding_tax_rate,
      gst_category: dto.gst_category,
      gst_treatment: dto.gst_treatment as any,
      invoice_submission_method: dto.invoice_submission_method,
      exemption_certificates: dto.exemption_certificates,
      typical_hsn_sac_codes: dto.typical_hsn_sac_codes,
      risk_rating: dto.risk_rating,
      payment_terms: dto.payment_terms,
      credit_limit: dto.credit_limit,
      currency: dto.currency,
      currency_symbol: dto.currency_symbol ?? "",
      tds_applicable: dto.tds_applicable,
      status: dto.status,
      vendor_name_embedding: dto.vendor_name_embedding,
      metadata: dto.metadata,
      created_at: dto.created_at,
      updated_at: dto.updated_at,
    };
  }

  /**
   * Map VendorSearchPaginationDTO to VendorSearchPagination
   */
  static toVendorSearchPagination(
    dto: VendorSearchPaginationDTO
  ): VendorSearchPagination {
    return {
      total: dto.total,
      limit: dto.limit,
      offset: dto.offset,
      has_more: dto.has_more,
    };
  }

  /**
   * Map VendorSearchResponseDTO to VendorSearchResultEntity
   */
  static toVendorSearchResult(
    dto: VendorSearchResponseDTO
  ): VendorSearchResultEntity {
    return {
      success: dto.success,
      data: dto.data.map((item) => this.toVendorSearchItemEntity(item)),
      pagination: this.toVendorSearchPagination(dto.pagination),
    };
  }

  /**
   * Map VendorResponseDTO to VendorOperationResult
   */
  static toVendorOperationResult(
    dto: VendorResponseDTO
  ): VendorOperationResult {
    const responseData = dto.data as {
      vendor_id?: number;
      vendorid?: string;
      zoho_vendorid?: string;
    } & Partial<VendorDataDTO>;

    if (responseData?.vendor_id != null) {
      return {
        success: dto.success,
        message: dto.message,
        data: {
          vendorid: String(responseData.vendor_id),
          zoho_vendorid: responseData.zoho_vendorid ?? "",
        } as VendorEntity,
      };
    }

    // Handle API response structure: data.vendor or data directly
    const vendorData = (dto.data as any)?.vendor || dto.data;

    return {
      success: dto.success,
      message: dto.message,
      data: vendorData ? this.toVendorEntity(vendorData) : undefined,
    };
  }

  /**
   * Map VendorDetailResponseDTO to VendorDetailResult
   */
  static toVendorDetailResult(dto: VendorDetailResponseDTO): VendorDetailResult {
    return {
      success: dto.success,
      message: dto.message,
      data: this.toVendorEntity(dto.data),
    };
  }
}

